import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@carbon/react';
import { CloudOffline, Rocket } from '@carbon/react/icons';
import {
  usePublishStorefrontMutation,
  useStorefront,
  useStorefrontPreviewLinkMutation,
  useUnpublishStorefrontMutation,
} from 'src/hooks/useStorefront';

/**
 * The storefront-wide actions, in the area header rather than the nav (§9): view, preview,
 * then publish — in the order someone works through them.
 *
 * "View storefront" is a plain link to the public address and is disabled while the storefront
 * is unpublished, because that address 404s until publish. The preview link is a separate
 * button: it asks the API for a signed, short-lived URL and opens it — the only way to see a
 * draft site, and the reason publish is not a prerequisite for reviewing your own work.
 *
 * Publish lives here, not in the Site form: it acts on the whole storefront, so it belongs on
 * every storefront page, and beside the form's own Save it read as a second way to save.
 */
const StorefrontHeaderActions = React.memo(() => {
  const { t } = useTranslation();
  const { data: storefront } = useStorefront();
  const previewLink = useStorefrontPreviewLinkMutation();
  const { mutate: publishStorefront, isLoading: publishing } = usePublishStorefrontMutation();
  const { mutate: unpublishStorefront, isLoading: unpublishing } = useUnpublishStorefrontMutation();

  const published = Boolean(storefront?.publishedAt);
  // From the API, not guessed from the portal's own hostname — the same authority the Site screen
  // reads, so the header and the screen cannot disagree about the site's address.
  // `STOREFRONT_BASE_DOMAIN` lives on the server and it composes the whole origin, scheme and port
  // included; deriving it from the last two labels of `window.location` produced
  // `https://pho-24.localhost` in development (no port, wrong scheme) and a dead link on any
  // deployment where the portal and the storefronts are not on one domain.
  const publicUrl = storefront?.publicUrl ?? null;

  return (
    <div className="storefront-header-actions">
      <Button
        kind="ghost"
        size="sm"
        disabled={!published || !publicUrl}
        href={publicUrl ?? undefined}
        target="_blank"
        rel="noreferrer"
      >
        {t('storefront.actions.view')}
      </Button>
      <Button
        kind="ghost"
        size="sm"
        disabled={previewLink.isLoading}
        onClick={() =>
          previewLink.mutate(undefined, {
            onSuccess: (response) => window.open(response.url, '_blank', 'noopener,noreferrer'),
          })
        }
      >
        {t('storefront.actions.preview')}
      </Button>
      {/* Publish is the step the whole area works towards, so it is the primary action here.
          Unpublish takes a live site down, so it stays a danger action rather than primary. */}
      {published ? (
        <Button
          kind="danger--tertiary"
          size="sm"
          renderIcon={CloudOffline}
          disabled={unpublishing}
          onClick={() => unpublishStorefront()}
        >
          {t('storefront.actions.unpublish')}
        </Button>
      ) : (
        <Button kind="primary" size="sm" renderIcon={Rocket} disabled={publishing} onClick={() => publishStorefront()}>
          {t('storefront.actions.publish')}
        </Button>
      )}
    </div>
  );
});

export default StorefrontHeaderActions;
